import React, { useEffect, useState } from 'react';
import { selector } from 'apps/services';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { getText } from 'helpers/app/getText';
import { ENGLISH_LANG_SHORT, LANGUAGES, REQUIRED, PSP_TYPES, LOCALE_DEFAULT_CONFIG, COUNTRIES, TOAST_AUTO_CLOSE, TOAST_POSITION } from 'configs/constants';
import { Button, Card, CardBody, FormGroup, Input, InputGroup, InputGroupText } from 'reactstrap';
import { attemptSearchReceipt } from 'apps/modules/receipt/ReceiptActions';
import { attemptSetLanguage } from 'apps/modules/app/AppActions';
import { getLocale } from 'helpers/app/getLocale';
import Datetime from 'react-datetime';
import Select from 'react-select';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Footer } from 'components/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { produce, setAutoFreeze } from 'immer';
import moment from 'moment';
import { isEmpty } from 'validator';
import { getProviderImageURL } from 'helpers/app/getProviderImageURL';
import { Link } from 'react-router-dom';
import i18n from '../../i18n';

function Receipt({ app, receipt, attemptSearchReceipt, attemptSetLanguage }) {
  dayjs.extend(utc);
  setAutoFreeze(false);

  const [fields, setFields] = useState({
    countryCode: '',
    receiptDate: '',
    pspType: '',
    customerInfoMasked: '',
    pointOfServiceName: '',
  });

  const [errors, setErrors] = useState({
    countryCode: '',
    receiptDate: '',
  });

  useEffect(() => {
    attemptSetLanguage(ENGLISH_LANG_SHORT);
  }, []);

  const onCountryChange = (opt) => {
    setFields(
      produce((draft) => {
        draft.countryCode = opt?.value ?? '';
      }),
    );
  };

  const onReceiptDateChange = (ev) => {
    setFields(
      produce((draft) => {
        draft.receiptDate = moment(ev)?.format('YYYY-MM-DD');
      }),
    );
  };

  const onPSPTypeChange = (val) => {
    setFields(
      produce((draft) => {
        draft.pspType = val;
      }),
    );
  };

  const validate = (name, value) => {
    switch (name) {
      case 'countryCode':
        if (isEmpty(value)) {
          return REQUIRED('Country', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
      case 'receiptDate':
        if (isEmpty(value)) {
          return REQUIRED('Receipt date', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
      case 'pspType':
        if (isEmpty(value)) {
          return REQUIRED('Payment method', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
    }
  };

  const onSearchReceipts = () => {
    let errorsObj = {};

    Object.keys(fields).map((name) => {
      const error = validate(name, fields[name]);
      if (error?.length > 0) {
        errorsObj[name] = error;
      }
    });

    if (Object.keys(errorsObj).length > 0) {
      setErrors(errorsObj);
      return;
    }

    setErrors({
      countryCode: '',
      receiptDate: '',
    });

    const params = {
      countryCode: fields.countryCode,
      receiptDate: fields.receiptDate,
      customerInfoMasked: fields.customerInfoMasked,
      ...(fields.pointOfServiceName && { pointOfServiceName: fields.pointOfServiceName }),
    };

    attemptSearchReceipt(params);
  };

  const onKeyUp = (evt, index) => {
    const { target, key } = evt;
    const maxlength = target.getAttribute('maxlength');
    const length = target.value.length;

    const el = document.getElementsByClassName('txtOtpInput');

    if (key !== 'Backspace') {
      if (parseInt(maxlength) == length && index < el.length - 1) {
        el[index + 1]?.focus();
      }
    } else if (el[index - 1]?.value) {
      el[index - 1].value = '';
      el[index - 1]?.focus();
    }
    setFields(
      produce((draft) => {
        draft.customerInfoMasked = el[0].value + el[1].value + el[2].value + el[3].value;
      }),
    );
  };

  const disablePastDt = (current) => {
    return current.isAfter(moment().subtract(3, 'month')) && current.isBefore(moment());
  };

  const onLanguageChange = (opt) => {
    const appLanguage = LANGUAGES.filter((lng) => lng.value === opt?.value);
    i18n.changeLanguage(appLanguage[0].locale);
    attemptSetLanguage(appLanguage[0].locale);
  };

  const language = app.get('language');
  const receipts = receipt.get('receipts');

  return (
    <div className="wrapper">
      <ToastContainer autoClose={TOAST_AUTO_CLOSE} position={TOAST_POSITION} />
      <div className="container d-flex flex-column">
        <div>
          <h2 className="text-center mt-10">
            <b>{getText('receipt:SEARCH_RECEIPT')}</b>
          </h2>
          <FormGroup>
            <label>{getText('receipt:COUNTRY')}:</label>
            <Select name="country" menuPlacement="bottom" className={`filter-control ${errors.countryCode && `input-error-msg`}`} placeholder={getText('receipt:COUNTRY')} isClearable={true} value={fields.countryCode?.length > 0 ? COUNTRIES?.find((ctry) => ctry.value === fields.countryCode) : ''} onChange={(val) => onCountryChange(val)} options={COUNTRIES.sort((a, b) => a.name?.localeCompare(b.name))} />
            <p className={`error-msg ${!errors.countryCode && 'hidden'}`}>{errors.countryCode}</p>
          </FormGroup>
          <FormGroup className="d-flex flex-column">
            <label>{getText('receipt:DATE')}:</label>
            <Datetime dateFormat="YYYY-MM-DD" isValidDate={disablePastDt} className={`${errors.receiptDate && `input-error-msg`}`} locale={dayjs.locale(getLocale(language), LOCALE_DEFAULT_CONFIG)} closeOnSelect={true} value={fields.receiptDate || ''} inputProps={{ placeholder: getText('receipt:DATE') }} timeFormat={false} onChange={(val) => onReceiptDateChange(val)} />
            <p className={`error-msg ${!errors.receiptDate && 'hidden'}`}>{errors.receiptDate}</p>
          </FormGroup>
          <FormGroup className="d-flex flex-column">
            <label>{getText('receipt:PAYMENT_METHOD_USED')}:</label>
            <div className="d-flex flex-row" style={{ gap: '10px' }}>
              <a onClick={() => onPSPTypeChange(PSP_TYPES.CREDITCARD.value)} className={`d-flex pointer payment-box ${fields.pspType === PSP_TYPES.CREDITCARD.value ? 'selected-box' : 'normal-box '}`}>
                <img src="img/provider-icons/icon_debit_card_black.svg" className="imgPaymentMethod" />
                <span className="payment-box-text">{PSP_TYPES.CREDITCARD.label}</span>
              </a>
              <a onClick={() => onPSPTypeChange(PSP_TYPES.APPLE_PAY.value)} className={`d-flex pointer payment-box ${fields.pspType === PSP_TYPES.APPLE_PAY.value ? 'selected-box' : 'normal-box '}`}>
                <img src="img/provider-icons/Apple_Pay_Mark_RGB_041619.svg" className="imgPaymentMethod" />
                <span className="payment-box-text">{PSP_TYPES.APPLE_PAY.label}</span>
              </a>
              <a onClick={() => onPSPTypeChange(PSP_TYPES.GOOGLE_PAY.value)} className={`d-flex pointer payment-box ${fields.pspType === PSP_TYPES.GOOGLE_PAY.value ? 'selected-box' : 'normal-box '}`}>
                <img src="img/provider-icons/google-pay-mark_800.svg" className="imgPaymentMethod" />
                <span className="payment-box-text">{PSP_TYPES.GOOGLE_PAY.label}</span>
              </a>
              <a onClick={() => onPSPTypeChange(PSP_TYPES.SWISH.value)} className={`d-flex pointer payment-box ${fields.pspType === PSP_TYPES.SWISH.value ? 'selected-box' : 'normal-box '}`}>
                <img src="img/provider-icons/Logo_Swish.svg" className="imgPaymentMethod" />
                <span className="payment-box-text">{PSP_TYPES.SWISH.label}</span>
              </a>
            </div>
          </FormGroup>
          <FormGroup className="d-flex flex-column">
            {fields.pspType === PSP_TYPES.CREDITCARD.value && <label>{getText('receipt:CREDIT_CARD_DIGITS')}:</label>}
            {fields.pspType === PSP_TYPES.APPLE_PAY.value && <label>{getText('receipt:APPLE_PAY_DIGITS')}:</label>}
            {fields.pspType === PSP_TYPES.GOOGLE_PAY.value && <label>{getText('receipt:GOOGLE_PAY_DIGITS')}:</label>}
            {fields.pspType === PSP_TYPES.SWISH.value && <label>{getText('receipt:SWISH_PAY_DIGITS')}:</label>}
            {fields.pspType?.length > 0 && (
              <div className="d-flex flex-row" id="otp-inputs" style={{ gap: '10px' }}>
                <input type="text" maxLength={1} onKeyUp={(ev) => onKeyUp(ev, 0)} className="txtOtpInput" />
                <input type="text" maxLength={1} onKeyUp={(ev) => onKeyUp(ev, 1)} className="txtOtpInput" />
                <input type="text" maxLength={1} onKeyUp={(ev) => onKeyUp(ev, 2)} className="txtOtpInput" />
                <input type="text" maxLength={1} onKeyUp={(ev) => onKeyUp(ev, 3)} className="txtOtpInput" />
              </div>
            )}
            <p className={`error-msg ${!errors.pspType && 'hidden'}`}>{errors.pspType}</p>
          </FormGroup>
          <Button color="success" className="pull-left btn-labeled mb-4" size="sm" onClick={onSearchReceipts}>
            <div className="btn-label">
              <FontAwesomeIcon icon={faSearch} size="xs" />
            </div>
            <span>{getText('receipt:SEARCH')}</span>
          </Button>
        </div>
        <div className="d-flex flex-column" style={{ gap: '10px', marginBottom: '135px' }}>
          {receipts?.length > 0 ? (
            receipts?.map((receipt, idx) => {
              return (
                <Link to={`/receipt/${receipt._id}`} target="_blank">
                  <Card outline key={idx} className="receipt-card">
                    <CardBody className="d-flex flex-row justify-content-between">
                      <div className="d-flex align-items-center">
                        <img src={getProviderImageURL(receipt.pspType, receipt.customerInfoMasked)} className="img-receipt" />
                      </div>
                      <div className="d-flex flex-column">
                        <span>{receipt.merchantName}</span>
                        <span>{receipt.pointOfServiceName}</span>
                      </div>
                      <div className="d-flex flex-column text-right">
                        <span>{dayjs.utc(receipt.receiptDate).local().format('YYYY-MM-DD HH:mm')}</span>
                        <span>{`${receipt.totalAmountInclVat} ${receipt.currency}`}</span>
                      </div>
                    </CardBody>
                  </Card>
                </Link>
              );
            })
          ) : (
            <p>{getText('notifications:NO_RECEIPTS_FOUND_MSG')}</p>
          )}
        </div>
      </div>
      <Footer pageLanguages={LANGUAGES} onLanguageChange={(opt) => onLanguageChange(opt)} defaultLanguage={language} footerBackgroundColor={''} />
    </div>
  );
}

const mapStateToProps = (state) => selector(state, false, ['authorisation', 'app', 'receipt']);

const mapDispatchToProps = (dispatch) => {
  return {
    attemptSearchReceipt: (data) => dispatch(attemptSearchReceipt(data)),
    attemptSetLanguage: (data) => dispatch(attemptSetLanguage(data)),
  };
};

export default withTranslation('receipt')(connect(mapStateToProps, mapDispatchToProps)(Receipt));
